import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "automation-listing" }
const _hoisted_2 = { class: "automation-listing__content" }
const _hoisted_3 = {
  key: 0,
  colspan: "100%",
  class: "automation-listing__empty-state__no-result"
}

import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import LayoutHeadingTabs from 'vcomponents/common/layout/LayoutHeadingTabs.vue';
import AutomationLanding from 'vcomponents/automation/listing/AutomationLanding.vue';
import AutomationCreateAutomation from 'vcomponents/automation/AutomationCreateAutomation.vue';
import AutomationEmptyStateDisconnectedToProvider from 'vcomponents/automation/AutomationEmptyStateDisconnectedToProvider.vue';
import AutomationListingEmptyStateNoResult from 'vcomponents/automation/listing/emptyState/AutomationListingEmptyStateNoResult.vue';
import AutomationListingTableHead from 'vcomponents/automation/listing/table/AutomationListingTableHead.vue';
import AutomationListingTableRow from 'vcomponents/automation/listing/table/AutomationListingTableRow.vue';
import ListingSettingsHeader from 'vcomponents/common/listing/header/ListingSettingsHeader.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import TablePagination from 'vcomponents/common/table/TablePagination.vue';
import {
    getAutomations as fetchAutomations,
    getAutomationTemplates,
} from 'api/automation/automationService.js';
import { useAutomationPieces } from 'composables/automation/useAutomationPieces';
import type { AutomationPiece } from 'composables/automation/useAutomationPieces';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';
import { AutomationEvent } from 'types/events';
import type { AutomationUpdatedVersionPayload } from 'types/events';

interface SortableConfig {
    direction: 'asc' | 'desc' | '';
    orderBy: string;
}

interface Column {
    order: number;
    label: string;
    code: string;
    isDefault: boolean;
    isMovable: boolean;
    isVisible: boolean;
    fixed?: 'left' | 'right';
    alignContent?: 'left' | 'center' | 'right';
    fitContent?: boolean;
    sortable?: SortableConfig;
    maxWidth?: string;
    minWidth?: string;
}

interface Pagination {
    limit: number;
    offset: number;
    total: number;
    currentPage: number;
}

interface AutomationTemplate {
    id: string;
    version: string;
    wizard_bindings: unknown[];
    wizard_schema: unknown[];
    wizard_steps: unknown[];
}

interface AutomationMetadata {
    template?: { id?: string; version?: string };
    wizard_steps?: WizardStep[];
    wizard_schema?: WizardStep[];
    wizard_bindings?: Record<string, unknown>;
    [key: string]: unknown;
}

interface WizardStep {
    fields?: WizardField[];
    [key: string]: unknown;
}

interface WizardField {
    id?: string;
    target?: unknown;
    [key: string]: unknown;
}

interface AutomationVersionInfo {
    applied: string;
    available: string;
    diff: string;
    updateType?: 'major' | 'minor';
}

interface AutomationVersion {
    displayName: string;
    trigger?: unknown;
}

interface Automation {
    id: string;
    name: string;
    date: string;
    status: boolean;
    realStatus: string;
    actions: boolean;
    version: AutomationVersion;
    metadata: AutomationMetadata;
    pieces: AutomationPiece[];
    hasTemplateVersionUpdate?: boolean;
    versionInfo?: AutomationVersionInfo;
}

interface ApiAutomationFlow {
    id: string;
    status: string;
    updated?: string;
    version: AutomationVersion;
    metadata: AutomationMetadata;
}

interface ApiAutomationTemplate {
    id: string;
    metadata?: {
        template?: { version?: string };
        wizard_bindings?: unknown[];
        wizard_schema?: unknown[];
        wizard_steps?: unknown[];
    };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AutomationListing',
  setup(__props) {

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();
const { initializePiecesList } = useAutomationPieces();

const translations: Record<string, string> = {
    title: $t.__('automation.AutomationLayout.title'),
    alertDescription: $t.__('automation.AutomationListing.alert.description'),
    alertLabel: $t.__('automation.AutomationListing.alert.action.label'),
};

const allAutomations = ref<Automation[]>([]);
const automations = ref<Automation[]>([]);
const automationNeedsUpdateVersion = ref<Automation[]>([]);
const templates = ref<AutomationTemplate[]>([]);
const isLoadingTable = ref<boolean>(true);
const searchValue = ref<string>('');
const isProviderDown = ref<boolean>(false);
const sort = ref<SortableConfig[]>([]);
const pagination = ref<Pagination>({
    limit: 25,
    offset: 0,
    total: 0,
    currentPage: 1,
});

const columns: Column[] = [
    {
        order: 0,
        label: $t.__('common.labels.name'),
        code: 'name',
        isDefault: true,
        isMovable: false,
        isVisible: true,
        sortable: { direction: '', orderBy: 'name' },
        maxWidth: '424px',
    },
    {
        order: 1,
        label: $t.__('common.labels.elements'),
        code: 'elements',
        fixed: 'right',
        isDefault: true,
        isMovable: false,
        isVisible: true,
        maxWidth: '245px',
        minWidth: '245px',
    },
    {
        order: 2,
        label: $t.__('common.labels.last_modification'),
        code: 'date',
        fixed: 'right',
        isDefault: true,
        isMovable: false,
        isVisible: true,
        fitContent: true,
        sortable: { direction: 'desc', orderBy: 'date' },
        maxWidth: '200px',
        minWidth: '200px',
    },
    {
        order: 3,
        label: $t.__('common.labels.status'),
        code: 'status',
        fixed: 'right',
        alignContent: 'center',
        isDefault: true,
        isMovable: false,
        isVisible: true,
        fitContent: true,
        sortable: { direction: '', orderBy: 'status' },
        maxWidth: '64px',
        minWidth: '64px',
    },
    {
        order: 4,
        label: '',
        code: 'actions',
        fixed: 'right',
        isDefault: true,
        isMovable: false,
        isVisible: true,
        fitContent: true,
        maxWidth: '64px',
        minWidth: '64px',
    },
];

const isAutomationPanelOpen = computed<boolean>(
    () => store.state.popupPanel.automationWizardPanel.display,
);
const currentOpenAutomation = computed<Automation | null>(
    () => store.state.popupPanel.automationWizardPanel.automation ?? null,
);
const actionAlert = computed(() => ({
    label: translations.alertLabel,
    callback: () => openAutomationModalUpdateVersion(),
}));
const shouldDisplayLanding = computed<boolean>(
    () => !isLoadingTable.value && allAutomations.value.length === 0 && !isProviderDown.value,
);

const initializeSort = (): void => {
    sort.value = columns.flatMap((column) =>
        column.sortable?.orderBy
            ? [{ orderBy: column.sortable.orderBy, direction: column.sortable.direction }]
            : [],
    );
};

const backwardCompatibilityForWizardSteps = (automation: Automation): Automation => {
    // See SAAS-31332 for details
    if (!automation?.metadata || automation.metadata.wizard_schema) {
        return automation;
    }
    const bindings: Record<string, unknown> = {};
    automation.metadata.wizard_bindings = bindings;
    automation.metadata.wizard_schema = _.cloneDeep(automation.metadata.wizard_steps);
    automation.metadata.wizard_schema?.forEach((step) => {
        if (!step?.fields) {
            return;
        }
        step.fields.forEach((field) => {
            if (field?.id && field?.target) {
                bindings[field.id] = field.target;
                delete field.target;
            }
        });
    });
    return automation;
};

const identifyTemplateVersionUsed = (automation: Automation): Automation => {
    const templateId = automation?.metadata?.template?.id;
    if (!templateId) {
        return automation;
    }
    const template = templates.value.find((tpl) => tpl.id === templateId);
    if (!template) {
        return automation;
    }

    const availableTemplateVersion = parseFloat(template.version);
    const appliedTemplateVersion = parseFloat(automation?.metadata?.template?.version ?? '1');
    const versionDiff = availableTemplateVersion - appliedTemplateVersion;

    if (versionDiff === 0) {
        return automation;
    }

    automation.hasTemplateVersionUpdate = true;

    const majorVersionDiff =
        Math.floor(availableTemplateVersion) - Math.floor(appliedTemplateVersion);
    const minorVersionDiff = (
        (availableTemplateVersion % 1) -
        (appliedTemplateVersion % 1)
    ).toFixed(2);

    automation.versionInfo = {
        applied: appliedTemplateVersion.toFixed(1),
        available: template.version,
        diff: versionDiff.toFixed(1),
    };

    if (majorVersionDiff > 0) {
        automation.versionInfo.updateType = 'major';
    } else if (parseFloat(minorVersionDiff) >= 0.1) {
        automation.versionInfo.updateType = 'minor';
        automationNeedsUpdateVersion.value.push(automation);
    }

    return automation;
};

const sortAutomations = (items: Automation[]): Automation[] => {
    for (const sortItem of sort.value) {
        if (!sortItem.direction) {
            continue;
        }
        items.sort((a, b) => {
            let comparison = 0;
            const orderBy = sortItem.orderBy;

            if (orderBy === 'date') {
                const aValue = new Date(a.date).getTime() || 0;
                const bValue = new Date(b.date).getTime() || 0;
                comparison = aValue - bValue;
            } else if (orderBy === 'name') {
                comparison = a.name.localeCompare(b.name, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            } else if (orderBy === 'status') {
                const normalizeStatus = (status: boolean | null): number => {
                    if (status === true) {
                        return 1;
                    }
                    if (status === false) {
                        return 0;
                    }
                    return -1;
                };
                comparison = normalizeStatus(a.status) - normalizeStatus(b.status);
            }

            return sortItem.direction === 'asc' ? comparison : -comparison;
        });
    }
    return items;
};

const updateAutomations = (): void => {
    if (!allAutomations.value) {
        return;
    }

    let filteredAutomations = [...allAutomations.value];

    // Backward Compatibility: Convert legacy wizard_steps to new wizard_schema + wizard_bindings structure
    // The old system used a single wizard_steps property, but we've split it into wizard_schema and wizard_bindings
    // to better handle automation template versioning (see SAAS-31332)
    filteredAutomations = filteredAutomations.map(backwardCompatibilityForWizardSteps);
    filteredAutomations = filteredAutomations.map(identifyTemplateVersionUsed);

    if (searchValue.value) {
        filteredAutomations = filteredAutomations.filter((automation) =>
            automation.name.toLowerCase().includes(searchValue.value.toLowerCase()),
        );
    }

    filteredAutomations = sortAutomations(filteredAutomations);

    pagination.value.total = filteredAutomations.length;
    pagination.value.offset =
        pagination.value.currentPage === 1
            ? 0
            : (pagination.value.currentPage - 1) * pagination.value.limit;
    const start = pagination.value.offset;
    const end = start + pagination.value.limit;
    automations.value = filteredAutomations.slice(start, end);
};

const loadAutomationTemplates = async (): Promise<void> => {
    const response = await getAutomationTemplates();
    templates.value = response.data.data.map((template: ApiAutomationTemplate) => ({
        id: template.id,
        version: template.metadata?.template?.version ?? '1',
        wizard_bindings: template.metadata?.wizard_bindings ?? [],
        wizard_schema: template.metadata?.wizard_schema ?? [],
        wizard_steps: template.metadata?.wizard_steps ?? [],
    }));
};

const getAutomations = (): void => {
    isLoadingTable.value = true;
    automationNeedsUpdateVersion.value = [];
    fetchAutomations()
        .then((response) => {
            allAutomations.value = response.data.data.map(
                (flow: ApiAutomationFlow): Automation => ({
                    id: flow.id,
                    name: flow.version.displayName,
                    date: flow.updated || '-',
                    status: flow.status === 'ENABLED',
                    realStatus: flow.status,
                    actions: true,
                    version: flow.version,
                    metadata: flow.metadata,
                    pieces: [],
                }),
            );
            updateAutomations();
            return initializePiecesList(allAutomations.value);
        })
        .catch((error) => {
            allAutomations.value = [];
            isProviderDown.value = true;
            store.state.system.errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                close: true,
                text: $t.__('common.labels.toast.error'),
            });
        })
        .finally(() => {
            isLoadingTable.value = false;
        });
};

const searchAttributes = (value: string): void => {
    searchValue.value = value;
    updateAutomations();
};

const updatePagination = (newPagination: Pagination): void => {
    pagination.value = {
        ...newPagination,
        offset: (newPagination.currentPage - 1) * newPagination.limit,
    };
    updateAutomations();
};

const updateAutomation = ({ id, status }: { id: string; status: boolean }): void => {
    const automation = allAutomations.value.find((item) => item.id === id);
    if (automation) {
        automation.status = status;
    }
    updateAutomations();
};

const handleSort = (sortParams: { orderBy: string }): void => {
    const orderTransitions: Record<'asc' | 'desc' | '', 'asc' | 'desc' | ''> = {
        asc: 'desc',
        desc: '',
        '': 'asc',
    };
    const existingSort = sort.value.find((item) => item.orderBy === sortParams.orderBy);
    if (existingSort) {
        existingSort.direction = orderTransitions[existingSort.direction];
        if (!existingSort.direction) {
            sort.value = sort.value.filter((item) => item.orderBy !== sortParams.orderBy);
        }
    } else {
        sort.value.push({ orderBy: sortParams.orderBy, direction: 'asc' });
    }
    updateAutomations();
};

const resetFilters = (): void => {
    searchValue.value = '';
    initializeSort();
    updateAutomations();
};

const openAutomationModalUpdateVersion = (): void => {
    store.dispatch('popupPanel/open', {
        key: 'automationModalUpdateVersion',
        props: {
            automations: automationNeedsUpdateVersion.value,
            templates: templates.value,
        },
    });
};

const handleEditWizardAutomation = (automation: Automation): void => {
    if (isAutomationPanelOpen.value && currentOpenAutomation.value?.id === automation.id) {
        return;
    }
    if (isAutomationPanelOpen.value) {
        store.dispatch('popupPanel/close', { key: 'automationWizardPanel' });
    }
    store.dispatch('popupPanel/open', {
        key: 'automationWizardPanel',
        props: {
            automation,
            isEditMode: true,
        },
    });
};

const updateVersionAutomation = (result: AutomationUpdatedVersionPayload): void => {
    getAutomations();

    if (result.success) {
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            close: true,
            icon: true,
            text: $t.__('automation.AutomationModalUpdateVersion.toast.success'),
        });
    } else if (result.failureCount === result.total) {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            close: true,
            icon: true,
            text: $t.__('automation.AutomationModalUpdateVersion.toast.error.all'),
        });
    } else {
        $pushToast({
            type: CompassEnums.ToastType.INFORMATIVE,
            close: true,
            icon: true,
            text: $t.__('automation.AutomationModalUpdateVersion.toast.error.partial', {
                success: result.successCount,
                failure: result.failureCount,
            }),
        });
    }
};

onMounted(async () => {
    EventBus.on(AutomationEvent.DELETED, () => getAutomations());
    EventBus.on(AutomationEvent.RENAMED, () => getAutomations());
    EventBus.on(AutomationEvent.UPDATED, () => getAutomations());
    EventBus.on(AutomationEvent.UPDATED_VERSION, (payload) => {
        updateVersionAutomation(payload);
    });
    await loadAutomationTemplates();
    initializeSort();
    getAutomations();
});

onBeforeUnmount(() => {
    EventBus.off(AutomationEvent.DELETED);
    EventBus.off(AutomationEvent.RENAMED);
    EventBus.off(AutomationEvent.UPDATED);
    EventBus.off(AutomationEvent.UPDATED_VERSION);
});

return (_ctx: any,_cache: any) => {
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassTableBody = _resolveComponent("CompassTableBody")!
  const _component_CompassTable = _resolveComponent("CompassTable")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (shouldDisplayLanding.value)
      ? (_openBlock(), _createBlock(AutomationLanding, { key: 0 }))
      : (isProviderDown.value)
        ? (_openBlock(), _createBlock(AutomationEmptyStateDisconnectedToProvider, {
            key: 1,
            class: "automation-listing__empty-state"
          }))
        : (_openBlock(), _createBlock(LayoutHeadingTabs, {
            key: 2,
            title: translations.title
          }, {
            actions: _withCtx(() => [
              _createVNode(AutomationCreateAutomation, { disabled: isLoadingTable.value }, null, 8 /* PROPS */, ["disabled"])
            ]),
            content: _withCtx(() => [
              (automationNeedsUpdateVersion.value.length > 0)
                ? (_openBlock(), _createBlock(_component_CompassAlert, {
                    key: 0,
                    type: _ctx.CompassEnums.AlertType.WARNING,
                    description: translations.alertDescription,
                    showClose: false,
                    action: actionAlert.value
                  }, null, 8 /* PROPS */, ["type", "description", "action"]))
                : _createCommentVNode("v-if", true),
              _createElementVNode("div", _hoisted_2, [
                _createVNode(ListingSettingsHeader, {
                  columns: columns,
                  enableColumnsSettings: false,
                  searchable: "",
                  searchValue: searchValue.value,
                  onSearch: searchAttributes
                }, null, 8 /* PROPS */, ["searchValue"]),
                (isLoadingTable.value)
                  ? (_openBlock(), _createBlock(TableLoading, { key: 0 }))
                  : (_openBlock(), _createBlock(_component_CompassTable, {
                      key: 1,
                      size: _ctx.CompassEnums.TableSize.LARGE,
                      border: "",
                      isCheckable: false,
                      class: "automation-listing__table"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(AutomationListingTableHead, {
                          columns: columns,
                          onSortTableHead: handleSort
                        }),
                        (!automations.value.length)
                          ? (_openBlock(), _createElementBlock("td", _hoisted_3, [
                              _createVNode(AutomationListingEmptyStateNoResult, { onResetFilters: resetFilters })
                            ]))
                          : (_openBlock(), _createBlock(_component_CompassTableBody, { key: 1 }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(automations.value, (automation, index) => {
                                  return (_openBlock(), _createBlock(AutomationListingTableRow, {
                                    key: index,
                                    automation: automation,
                                    columns: columns,
                                    onUpdateAutomation: updateAutomation,
                                    onAutomationDuplicated: getAutomations,
                                    onEditWizardAutomation: handleEditWizardAutomation
                                  }, null, 8 /* PROPS */, ["automation"]))
                                }), 128 /* KEYED_FRAGMENT */))
                              ]),
                              _: 1 /* STABLE */
                            }))
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["size"])),
                (pagination.value.total > 0)
                  ? (_openBlock(), _createBlock(TablePagination, {
                      key: 2,
                      pagination: pagination.value,
                      isLoading: isLoadingTable.value,
                      onUpdatePagination: updatePagination
                    }, null, 8 /* PROPS */, ["pagination", "isLoading"]))
                  : _createCommentVNode("v-if", true)
              ])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["title"]))
  ]))
}
}

})